import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, CircularProgress, IconButton } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import CopyToClipboard from 'react-copy-to-clipboard';
import clsx from 'clsx';

import { getRequest } from 'utils/api';
import { openModal, setFooter } from 'features/utilsSlice';
import { getGigOwner } from 'utils/helpers';
import { getMasquerading } from 'utils/localStorage';
import Toast from 'utils/toast';
import useCurrentUser from 'hooks/useCurrentUser';
import useCurrentProfile from 'hooks/useCurrentProfile';
import FullscreenIcon from 'public/icons/fullscreen.svg';
import NoAvatarImage from 'public/images/no-avatar.svg';
import TypingSection from './TypingSection';
import ChatMessage from './ChatMessage';
import IconMainContainer from 'components/IconMainContainer';
import ProfileDialog from 'components/profile/ProfileDialog';
import AdvertiserProfileDialog from 'components/advertiserProfile/AdvertiserProfileDialog';
import styles from 'styles/components/gigs/Chat.module.scss';

const progressStyle = { marginTop: 50 };
const styleAlert = { margin: '15px auto' };
const size = { width: 30, height: 30 };

const Chat = ({ gig, isDialog, hasMilestones }) => {
  const [messages, setMessages] = useState([]);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState({});
  const [masquerading, setMasquerading] = useState(false);
  const [domReady, setDomReady] = useState(false);

  const router = useRouter();
  const { tab } = router.query;

  const dispatch = useDispatch();
  const currentProfile = useCurrentProfile();
  const endRef = useRef(null);

  const currentUser = useCurrentUser();
  const isGigOwner = getGigOwner(currentUser, gig?.user_id);

  const gigMessages = useSelector(state => state.gigs.gigMessages);

  const getMilestone = message => {
    if (!hasMilestones) return null;

    if (message.conversation_type === 'Milestone') {
      return gig.milestones.find(m => m.id === message.conversation_id);
    }

    return null;
  };

  const removeFile = () => setFile(null);

  const getMessageBody = message => {
    const linkRegex = /(https?:\/\/[^\s]+)/g;

    const match = message.match(linkRegex);

    if (!match) return message;

    const url = match[0];
    const parts = message.split(url);

    return (
      <>
        {parts[0] !== url && <span>{parts[0]}</span>}

        <CopyToClipboard
          text={url}
          onCopy={() => {
            Toast('success', 'Copied message link to clipboard!', {
              icon: <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />,
            });
          }}
        >
          <span className="color-main">{url}</span>
        </CopyToClipboard>

        {parts[1] !== url && <span>{parts[1]}</span>}
      </>
    );
  };

  const getDisplayName = sender => {
    if (sender.type === 'Profile') {
      return currentProfile?.id === sender.id ? 'Me' : sender.name;
    } else {
      return currentUser?.id === sender.id ? 'Me' : sender.name;
    }
  };

  const handleOpenProfile = () => {
    if (!gig?.user || gig?.profile) return; // For now till we have profile and user on gig

    if (isGigOwner || gig.user.role === 'influencer') {
      dispatch(
        openModal({
          name: 'Profile',
          data: { username: isGigOwner ? gig.profile.username : gig.user.profile_id },
        })
      );
    } else {
      dispatch(openModal({ name: 'Advertiser Profile', data: { username: gig.user.username } }));
    }
  };

  useEffect(() => {
    setIsLoading(prevState => ({ ...prevState, messages: true }));
    const endpoint = `gigs/${gig.id}/messages`;

    getRequest({
      endpoint,
    })
      .then(messages => {
        setMessages(messages);
        setIsLoading(prevState => ({ ...prevState, messages: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, messages: false })));
  }, [gig?.id]);

  useEffect(() => {
    dispatch(setFooter(false));

    return () => dispatch(setFooter(true));
  }, [dispatch]);

  useEffect(() => setDomReady(true), []);

  useEffect(() => {
    const endDiv = endRef.current;

    if (domReady && endDiv && !isLoading.messages) {
      return (endDiv.scrollTop = endDiv.scrollHeight);
    }
  }, [messages, domReady, isLoading.messages]);

  useEffect(() => {
    const masquerading = getMasquerading();
    setMasquerading(masquerading);
  }, []);

  useEffect(() => {
    const gigMessageId = gigMessages[gig.id]?.id;

    if (!gigMessageId) return;

    if (
      (gigMessageId > messages[messages.length - 1]?.id && !messages.includes(gigMessageId)) ||
      (gigMessageId && messages.length === 0)
    ) {
      setMessages(prevState => [...prevState, gigMessages[gig.id]]);
    }
  }, [gig.id, gigMessages, messages]);

  const getTitle = () => {
    if (gig.title) return gig.title;
    if (gig.profile?.display_name) return gig.profile.display_name;
    if (gig.user?.display_name) return gig.user.display_name;
    return 'Chat';
  };

  return (
    <div className={styles.chatContainer}>
      <div>
        <div
          className={styles.header}
          style={{ justifyContent: tab ? 'space-between' : 'center' }}
          onClick={handleOpenProfile}
        >
          {gig?.avatar_url && (
            <Avatar style={size} src={gig.avatar_url}>
              <NoAvatarImage />
            </Avatar>
          )}

          <h2 style={tab ? { flex: 1 } : {}}>{getTitle()}</h2>

          {!isDialog && (
            <IconButton
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                dispatch(openModal({ name: 'Full Screen Chat' }));
              }}
            >
              <FullscreenIcon width={18} height={18} />
            </IconButton>
          )}
        </div>
      </div>

      <div
        className={clsx(
          styles.messages,
          { [styles.height]: !gig.funded && isGigOwner },
          {
            [styles.masqueradingMessages]:
              masquerading ||
              (currentUser?.full_management_agency && currentUser?.currently_managing_profile),
          },
          { [styles.greenBoxHeight]: gig.status === 'completed' && isGigOwner },
          { [styles.maxHeight]: isDialog }
        )}
        ref={endRef}
      >
        {isLoading.messages && <CircularProgress color="primary" style={progressStyle} />}

        {messages.length === 0 && !isLoading.messages ? (
          <Alert style={styleAlert} severity="info">
            No messages
          </Alert>
        ) : (
          <>
            {messages.map(message => {
              const milestone = getMilestone(message);

              return (
                <ChatMessage
                  key={message.id}
                  message={message}
                  milestone={milestone}
                  getMessageBody={getMessageBody}
                  getDisplayName={getDisplayName}
                />
              );
            })}
          </>
        )}
      </div>

      <TypingSection
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        removeFile={removeFile}
        file={file}
        setFile={setFile}
        gig={gig}
      />

      <ProfileDialog />
      <AdvertiserProfileDialog />
    </div>
  );
};

Chat.propTypes = {
  gig: PropTypes.object.isRequired,
  isDialog: PropTypes.bool,
  hasMilestones: PropTypes.bool,
};

Chat.defaultProps = {
  isDialog: false,
  hasMilestones: true,
};

export default Chat;
